import React, { useMemo } from 'react';
import styled from 'styled-components';
import { trackEvent } from '@99designs/tracking';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { support } from '../../../../shared/lib/platformUrls';
import { colors } from '../../../../shared/lib/styles';
import { useIntl } from 'react-intl';
import Link, { ChameleonLink } from '../../../../shared/components/Link';
import Paragraph from '../../../../shared/components/Paragraph';
import Box from '../../../../shared/spa/core-components/Box';
import { FlexibleColumnFAQs, QuestionAnswerProps } from '../../FAQs';

const StyledParagraph = styled(Paragraph)`
    color: ${colors.typography.soft};
`;

function getFAQs(languageCode: string): QuestionAnswerProps[][] {
    let webBuilderCostMessage = __(
        'This depends on the web builder you choose and their pricing structures. Some web builders offer free plans or trial periods, but you can find out more via our web builder <Link>comparison chart</Link>.'
    );
    if (languageCode === 'fr') {
        webBuilderCostMessage = `Cela dépend du créateur de site que vous choisissez et de sa structure de prix.Certains créateurs de site web offrent des options ou périodes d'essai gratuites. D’autres proposent uniquement un abonnement mensuel / annuel. Ces coûts ne sont pas inclus dans la prestation de design offerte par 99designs. Vous pouvez trouver plus d'infos sur votre créateur de site web en consultant notre <Link>tableau comparatif</Link>.`;
    }
    return [
        [
            {
                question: __('What is a web builder?'),
                id: 'web-builder-explanation',
                answer: (
                    <StyledParagraph>
                        {__(
                            'Web builders allow anyone to manage a website using simple tools. They offer website templates that cater to a wide range of industries and are considered the simplest way to set up a website, particularly if you have little or no technical experience. You can edit text, images, and styles without writing a single line of code!'
                        )}
                    </StyledParagraph>
                ),
            },
            {
                question: __('What can my designer do?'),
                id: 'role-of-designer',
                answer: (
                    <StyledParagraph>
                        {__(
                            'Your designer can guide you through your initial website build and help you set it up. They will then begin collaborating with you to create a unique and customized design for your website.'
                        )}
                    </StyledParagraph>
                ),
            },
        ],
        [
            {
                question: __('Is web hosting included?'),
                id: 'web-hosting',
                answer: (
                    <StyledParagraph>
                        {__(
                            'Web hosting is an online space that you buy which allows you to create and store (host) content on your website. Most web builders will take care of web hosting for you.'
                        )}
                    </StyledParagraph>
                ),
            },
            {
                question: __('What is a domain name?'),
                id: 'domain-name',
                answer: (
                    <StyledParagraph>
                        {__(
                            'A domain name is your unique address on the web, where visitors can find your website. You can choose your domain name, or connect an existing one when you set up your web builder.'
                        )}
                    </StyledParagraph>
                ),
            },
        ],
        [
            {
                question: __('How long does the process take?'),
                id: 'process-length',
                answer: (
                    <StyledParagraph>
                        {__(
                            'The time it takes to build and design a website varies depending on its purpose and the amount of content. However, the benefit of using a web builder and a professional designer, means that the process is simplified and much faster than if you were to hire a developer. Whilst some websites can be ready within a week, the designer we match you with will be able to estimate how long it should take to complete and launch your website.'
                        )}
                    </StyledParagraph>
                ),
            },
            {
                question: __('How much does a web builder cost?'),
                id: 'cost',
                answer: (
                    <StyledParagraph>
                        <FormattedReactMessage
                            message={webBuilderCostMessage}
                            replacements={{
                                Link: <ChameleonLink href="#compare" />,
                            }}
                        />
                    </StyledParagraph>
                ),
            },
        ],
    ];
}

interface Props {
    languageCode: string;
}

function FAQSection({ languageCode }: Props) {
    const intl = useIntl();
    const locale = intl.locale;
    const faqs = getFAQs(languageCode);

    function handleOpen(faq: QuestionAnswerProps) {
        trackEvent('Clicked FAQ on Product Page', {
            category: 'web-builder',
            id: faq.id,
            title: faq.question,
        });
    }

    return (
        <>
            <FlexibleColumnFAQs faqSections={faqs} onOpen={handleOpen} />
            <Box display="flex" justifyContent="flex-end" paddingTop="space8">
                <Link href={support.helpCenter(locale)}>{__('Read more FAQs')}</Link>
            </Box>
        </>
    );
}

export default FAQSection;
